import { safeJsonParse } from 'src/core/helpers/json-parse.helper';
import { CampaignTestNotificationPayload } from 'src/core/models/notification/campaign-test-notification.payload';
import { TestNotificationComponent } from './components/test-notification/test-notification.component';
import { CampaignTestNotificationComponent } from './components/campaign-test-notification/campaign-test-notification.component';
import { ContactImportNotificationComponent } from './components/contact-import-notification/contact-import-notification.component';
import { NotificationTypeRegistration } from './models/notification-type-registration.model';
import { PermissionConstants } from 'src/core/constants/permission.constant';

function parseCampaignPayload(payload: string): CampaignTestNotificationPayload | null {
  return safeJsonParse<CampaignTestNotificationPayload>(payload);
}

/**
 * Lightweight type-to-component registry — a plain lookup object, not a service/decorator. Add a new
 * entry here whenever a new concrete `NotificationDto.type` needs its own rendering, click target, toast
 * behavior, and/or toast content; anything not listed falls back to the generic renderer with no
 * navigation and the generic "New notification received" toast (see
 * `resolve-notification-item-component.helper.ts`).
 */
export const NOTIFICATION_TYPE_COMPONENT_MAP: Record<string, NotificationTypeRegistration> = {
  TestNotification: {
    component: TestNotificationComponent,
    // Synthetic — sent from the bell's own "Test" screen. There's nowhere meaningful to navigate to, so
    // a click just marks it as read.
    resolveTargetUrl: () => null,
    showToast: true,
  },
  SilentTestNotification: {
    // Same rendering as TestNotification (identical payload shape) — only the toast behavior differs.
    component: TestNotificationComponent,
    resolveTargetUrl: () => null,
    showToast: false,
  },
  CampaignTestNotification: {
    component: CampaignTestNotificationComponent,
    resolveTargetUrl: payload => {
      const parsed = parseCampaignPayload(payload);
      const campaignId = parsed?.campaignId ?? parsed?.CampaignId;
      return campaignId != null ? `/campaigns/${campaignId}` : null;
    },
    showToast: true,
    resolveToastMessage: payload => {
      const parsed = parseCampaignPayload(payload);
      const campaignName = parsed?.campaignName ?? parsed?.CampaignName ?? '';
      return {
        titleKey: 'AgenticCX::Notification:Test:CampaignToastTitle',
        messageKey: 'AgenticCX::Notification:Test:CampaignToastMessage',
        messageParams: [campaignName],
        // PrimeNG's toast always prefixes this with "pi " (see ToasterService.show()) — it only works
        // with PrimeIcons glyphs, not FontAwesome, so this can't literally match the row's `fas fa-bullhorn`
        // icon. `pi-megaphone` is the closest PrimeIcons equivalent; the color class still applies either way.
        iconClass: 'pi-megaphone acx-toast-icon--primary',
      };
    },
  },
  ContactImportCompletedNotification: {
    component: ContactImportNotificationComponent,
    // The row click navigates to the contacts list (`/contacts`), as required by the import user story's
    // AC #2. The target is static because the payload carries no per-import filter and the contacts list
    // has no "imported batch" filter. The row's own "View" action still opens the detailed result dialog
    // in place and stops propagation, so it deliberately does not navigate.
    resolveTargetUrl: () => '/contacts',
    // Importing contacts does not imply seeing the contacts page; without it the row only marks as read.
    requiredPolicy: PermissionConstants.canViewContacts,
    // No toast on purpose. An import finishes seconds after it is started, so a toast landed on the
    // contacts page as an unsolicited success/failure popup right after the upload. The outcome is meant
    // to be pulled, not pushed: the row appears in the bell and its "View" opens the detailed result.
    showToast: false,
  },
};
